// Libraries
import React from 'react';

// Supermove
import {DataLoader, ScrollView, Space, Styled} from '@supermove/components';
import {gql} from '@supermove/graphql';
import {
  useResponsive,
  useQuery,
  useScrollView,
  useEffect,
  useDebouncedCallback,
  useState,
  useTextInput,
  ScrollViewType,
  TextInputType,
} from '@supermove/hooks';
import {ThreadModel, UserModel} from '@supermove/models';
import {colors, Typography} from '@supermove/styles';
import {pluralize} from '@supermove/utils';

// App
import EmptyState from '@shared/design/components/EmptyState';
import Line from '@shared/design/components/Line';
import SearchBar from '@shared/design/components/SearchBar';
import CommentForm, {FormikCommentFormType} from '@shared/modules/Comment/forms/CommentForm';
import useCreateCommentMutation from '@shared/modules/Comment/hooks/useCreateCommentMutation';
import ProjectCommentInput from '@shared/modules/Project/components/widgets/components/ProjectCommentInput';
import ProjectCommentsListItem from '@shared/modules/Project/components/widgets/components/ProjectCommentsListItem';
import WidgetBuilder from '@shared/modules/Project/components/widgets/components/WidgetBuilder';

const Row = Styled.View`
  flex-direction: row;
  justify-content: space-between;
  align-items: center;
  width: 100%;
`;

const FooterContainer = Styled.View`
`;

const SearchResultsText = Styled.Text`
  ${Typography.Responsive.Body};
  color: ${colors.gray.secondary};
`;

const CommentsSearch = ({
  officeThread,
  handleChangeQuery,
  searchQueryExists,
  searchInput,
}: {
  officeThread: ThreadModel;
  handleChangeQuery: (query: string) => void;
  searchQueryExists: boolean;
  searchInput: TextInputType;
}) => {
  const responsive = useResponsive();
  return (
    <React.Fragment>
      <SearchBar
        inputRef={searchInput.ref}
        onChangeText={(text) => handleChangeQuery(text)}
        placeholder={'Search'}
        containerStyle={{flex: 1}}
        style={{width: '100%'}}
        defaultValue={''}
        isClearable
        isResponsive
      />
      {searchQueryExists && (
        <React.Fragment>
          <Space height={8} />
          <Row>
            <SearchResultsText responsive={responsive}>
              {pluralize('result', officeThread.comments.length, true)} found
            </SearchResultsText>
          </Row>
        </React.Fragment>
      )}
    </React.Fragment>
  );
};

const Body = ({
  officeThread,
  viewer,
  commentScrollView,
  refetch,
}: {
  officeThread: ThreadModel;
  viewer: UserModel;
  commentScrollView: ScrollViewType;
  refetch: () => void;
}) => {
  const {commentsCount} = officeThread;
  if (!commentsCount) {
    return (
      <EmptyState.ContentContainer>
        <EmptyState
          title={'No Comments'}
          message={'Use the fields below to add comments\nto this project.'}
        />
      </EmptyState.ContentContainer>
    );
  }

  return (
    <BodyCommentList
      viewer={viewer}
      commentScrollView={commentScrollView}
      officeThread={officeThread}
      refetch={refetch}
    />
  );
};

const BodyCommentList = ({
  officeThread,
  viewer,
  commentScrollView,
  refetch,
}: {
  officeThread: ThreadModel;
  viewer: UserModel;
  commentScrollView: ScrollViewType;
  refetch: () => void;
}) => {
  const [dirtyCommentIds, setDirtyCommentIds] = useState([]);
  const {comments} = officeThread;

  useEffect(() => {
    commentScrollView.handleScrollToEnd({animated: true});
  }, [commentScrollView]);

  return (
    <ScrollView ref={commentScrollView.ref}>
      {comments.map((comment, index) => (
        <ProjectCommentsListItem
          key={`${comment.id}_${comment.subject}_${comment.body}`}
          comment={comment}
          viewer={viewer}
          refetch={refetch}
          index={index}
        />
      ))}
    </ScrollView>
  );
};

const Footer = ({form, handleSubmit}: {form: FormikCommentFormType; handleSubmit: () => void}) => (
  <React.Fragment>
    <Line />
    <FooterContainer>
      <ProjectCommentInput form={form} handleSubmit={handleSubmit} />
    </FooterContainer>
  </React.Fragment>
);

const ProjectCommentsContent = ({
  viewer,
  officeThread,
  refetch,
  searchQuery,
  setSearchQuery,
}: {
  viewer: UserModel;
  officeThread: ThreadModel;
  refetch: () => void;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
}) => {
  const searchInput = useTextInput();
  const commentScrollView = useScrollView();
  const handleChangeQuery = useDebouncedCallback((query) => setSearchQuery(query), 250);

  const commentForm = CommentForm.new({
    threadId: officeThread.id,
    userId: viewer.id,
  });

  const resetForm = () => {
    form.setFieldValue('commentForm.body', '');
    form.setFieldValue('commentForm.subject', '');
  };

  const {form, handleSubmit} = useCreateCommentMutation({
    commentForm,
    onSuccess: () => {
      setSearchQuery('');
      searchInput.handleClear();
      refetch();
      resetForm();
    },
    onError: (errors) => {
      console.log({errors});
    },
  });

  return (
    <React.Fragment>
      <WidgetBuilder.Header>
        <CommentsSearch
          officeThread={officeThread}
          handleChangeQuery={handleChangeQuery}
          searchQueryExists={!!searchQuery}
          searchInput={searchInput}
        />
      </WidgetBuilder.Header>
      <Body
        officeThread={officeThread}
        viewer={viewer}
        commentScrollView={commentScrollView}
        refetch={refetch}
      />
      <Footer form={form} handleSubmit={handleSubmit} />
    </React.Fragment>
  );
};

const ProjectComments = ({projectUuid}: {projectUuid: string}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const {loading, data, refetch} = useQuery(ProjectComments.query, {
    fetchPolicy: 'cache-and-network',
    variables: {projectUuid, searchQuery},
  });

  return (
    <WidgetBuilder.ContentContainer>
      <DataLoader loading={loading} data={data} as={WidgetBuilder.Loader}>
        {({loadedData}) => {
          return (
            <ProjectCommentsContent
              viewer={loadedData.viewer}
              officeThread={loadedData.project.officeThread}
              refetch={refetch}
              searchQuery={searchQuery}
              setSearchQuery={setSearchQuery}
            />
          );
        }}
      </DataLoader>
    </WidgetBuilder.ContentContainer>
  );
};

// --------------------------------------------------
// Data
// --------------------------------------------------
ProjectComments.query = gql`
  ${ProjectCommentsListItem.fragment}

  query ProjectComments(
    $projectUuid: String!,
    $searchQuery: String,
  ) {
    ${gql.query}
    viewer {
      id
    }
    project(uuid: $projectUuid) {
      id
      officeThread {
        id
        commentsCount
        comments: filteredComments(searchQuery: $searchQuery) {
          id
          ...ProjectCommentsListItem
        }
      }
    }
  }
`;

export default ProjectComments;
